import React, { useContext, useEffect, useMemo, useState } from "react";
import "../../Tabels/Tabels.css";
import "./AppSetup.css";
import Header from "../../Header/Header";
import { Context } from "../../../utils/context";
import toast, { Toaster } from "react-hot-toast";
import { Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { putData } from "../../../utils/api";
import { IDS } from "../../../utils/common";
import ModalSave from "../../common/ModelSave";

/* ------------------------------------------------------------------ */
/* App Setup — profile-style settings page                             */
/* Same endpoints, field names, validation rules and FormData payload  */
/* as the edit page (./Edit.js); values are read-only until "Edit".    */
/* ------------------------------------------------------------------ */

const EMAIL_PATTERN =
  /^([A-Za-z0-9_\-\.])+\@(?!(?:[A-Za-z0-9_\-\.]+\.)?([A-Za-z0-9_\-\.]+)\.\2)([A-Za-z0-9_\-\.]+\.)+([A-Za-z]{2,4})$/;

// API values may be numbers; length checks work on the text the user sees
const textLength = (value) => String(value ?? "").length;

// digits only, without blocking navigation/editing keys or shortcuts
const ALLOWED_KEYS = [
  "Backspace",
  "Delete",
  "Tab",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
];
const digitsOnly = (e) => {
  if (ALLOWED_KEYS.includes(e.key) || e.ctrlKey || e.metaKey) return;
  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
};

/* Label + control + error/hint. Module level so inputs keep focus. */
const SetupField = ({ name, label, hint, full = false, error, showHint, children }) => (
  <div className={classNames("app-setup__field", { "app-setup__field--full": full })}>
    <Form.Label htmlFor={`app-setup-${name}`} className="app-setup__label">
      {label}
    </Form.Label>
    {children}
    {error ? (
      <span className="app-setup__error" role="alert">
        {error.message}
      </span>
    ) : hint && showHint ? (
      <span className="app-setup__hint">{hint}</span>
    ) : null}
  </div>
);

/* Stored path -> server URL; newly picked file -> object URL (revoked on change). */
const useImagePreview = (value, baseUrl) => {
  const file =
    value && typeof value !== "string" && value.length > 0 && value[0] instanceof File
      ? value[0]
      : null;
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => objectUrl && URL.revokeObjectURL(objectUrl), [objectUrl]);
  if (objectUrl) return objectUrl;
  return typeof value === "string" && value ? baseUrl + value : null;
};

const Tables = () => {
  const { getData, isAllow, IMG_URL } = useContext(Context);
  const canEdit = !!isAllow?.includes(IDS.AppSetup.Edit);

  const [recordId, setRecordId] = useState(null);
  const [loadedValues, setLoadedValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showModal, setShowModal] = useState({ code: 0, message: "" });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  const logoValue = watch("logo");
  const qrValue = watch("qr_image");

  /* ---------- load: list -> first record -> record detail ---------- */
  const loadSetup = async () => {
    setLoading(true);
    setLoadError("");
    try {
      let id = recordId;
      if (!id) {
        const list = await getData(`/admin/setting/app-setup?page=1&per_page=5&term=`);
        id = list?.data?.data?.[0]?.id;
        if (!id) {
          setLoadError(
            list?.success === false
              ? list?.message || "Unable to load app setup."
              : "No app setup record found."
          );
          return;
        }
        setRecordId(id);
      }
      const response = await getData(`/admin/setting/app-setup/${id}`);
      if (!response?.data) {
        setLoadError(response?.message || "Unable to load app setup.");
        return;
      }
      setLoadedValues(response.data);
      reset(response.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSetup();
  }, []);

  const startEditing = () => {
    setSaveError("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    reset(loadedValues || {});
    setSaveError("");
    setIsEditing(false);
  };

  /* ---------- save (payload identical to Edit.js) ---------- */
  const onSubmit = async (data) => {
    setSaving(true);
    setSaveError("");
    try {
      const finalData = new FormData();
      finalData.append("website_name", data?.website_name);
      finalData.append("contact_no", data?.contact_no);
      finalData.append("alt_contact_no", data?.alt_contact_no);
      finalData.append("email", data?.email);
      finalData.append("address", data?.address);
      finalData.append("second_address", data?.second_address);
      finalData.append("cancel_period", data?.cancel_period);
      finalData.append("delivery_price", data?.delivery_price);
      finalData.append("minimum_order", data?.minimum_order);
      finalData.append("lat", data?.lat);
      finalData.append("long", data?.long);
      finalData.append("return_charges", data?.return_charges);
      finalData.append("return_period", data?.return_period);
      finalData.append("logo", data?.logo?.[0]);
      finalData.append("qr_image", data?.qr_image?.[0]);
      finalData.append("low_stock_day", data?.low_stock_day);
      finalData.append(
        "ship_rocket_pickup_location",
        data?.ship_rocket_pickup_location
      );
      finalData.append(
        "ship_rocket_pickup_pincode",
        data?.ship_rocket_pickup_pincode
      );

      const response = await putData(
        `/admin/setting/app-setup/${recordId}`,
        finalData
      );

      if (response?.success) {
        setShowModal({ code: response.code, message: response.message });
        setTimeout(() => setShowModal({ code: 0, message: "" }), 1000);
        setIsEditing(false);
        await loadSetup();
      } else {
        const message =
          typeof response?.errors === "string"
            ? response.errors
            : response?.message || "Could not save app setup. Please try again.";
        setSaveError(message);
        toast.error(message);
      }
    } catch (error) {
      console.log(error);
      setSaveError("Could not save app setup. Please try again.");
      toast.error("Could not save app setup. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onInvalid = () => {
    toast.error("Please fix the highlighted fields.");
  };

  /* ---------- field helpers ---------- */
  const fieldProps = (name) => ({
    id: `app-setup-${name}`,
    readOnly: !isEditing,
    "aria-invalid": errors?.[name] ? "true" : "false",
    className: classNames("app-setup__input", { "is-invalid": errors?.[name] }),
  });

  const fieldMeta = (name) => ({
    name,
    error: errors?.[name],
    showHint: isEditing,
  });

  const logoPreview = useImagePreview(logoValue, IMG_URL);
  const qrPreview = useImagePreview(qrValue, IMG_URL);

  return (
    <>
      <div className="main-advancedashboard">
        <Header title={"App Setup"} link={"/settings/app-setup"} />
        <section className="AdvanceDashboard">
          <div className="col-lg-12 p-0">
            <div className="row MainRowsec me-0 ms-0">
              <section className="Tabels tab-radio mt-3">
                <div className="app-setup">
                  {loading && !loadedValues ? (
                    <div className="app-setup__state" role="status">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading app setup…
                    </div>
                  ) : loadError ? (
                    <div className="app-setup__state app-setup__state--error" role="alert">
                      <ErrorOutlineIcon fontSize="small" className="me-2" />
                      {loadError}
                      <button
                        type="button"
                        className="btn app-setup__btn app-setup__btn--ghost ms-3"
                        onClick={loadSetup}
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    <Form
                      noValidate
                      onSubmit={handleSubmit(onSubmit, onInvalid)}
                      className={classNames("app-setup__form", {
                        "is-editing": isEditing,
                      })}
                    >
                      {/* ---------- profile header ---------- */}
                      <div className="app-setup__header">
                        <div className="app-setup__identity">
                          <div className="app-setup__avatar">
                            {typeof loadedValues?.logo === "string" && loadedValues.logo ? (
                              <img src={IMG_URL + loadedValues.logo} alt="" />
                            ) : (
                              <span aria-hidden="true">
                                {(loadedValues?.website_name || "A").charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <h2 className="app-setup__title">
                              {loadedValues?.website_name || "App Setup"}
                            </h2>
                            <p className="app-setup__subtitle">
                              {isEditing
                                ? "Editing — update the fields and save your changes."
                                : "Store-wide settings used by the app and website."}
                            </p>
                          </div>
                        </div>

                        <div className="app-setup__actions">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="btn app-setup__btn app-setup__btn--ghost"
                                onClick={cancelEditing}
                                disabled={saving}
                              >
                                <CloseIcon fontSize="small" />
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="btn app-setup__btn app-setup__btn--primary"
                                disabled={saving}
                              >
                                {saving ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <SaveOutlinedIcon fontSize="small" />
                                )}
                                {saving ? "Saving…" : "Save"}
                              </button>
                            </>
                          ) : (
                            canEdit && (
                              <button
                                type="button"
                                className="btn app-setup__btn app-setup__btn--primary"
                                onClick={startEditing}
                              >
                                <EditOutlinedIcon fontSize="small" />
                                Edit
                              </button>
                            )
                          )}
                        </div>
                      </div>

                      {saveError && (
                        <div className="app-setup__banner" role="alert">
                          <ErrorOutlineIcon fontSize="small" />
                          <span>{saveError}</span>
                        </div>
                      )}

                      <fieldset disabled={saving} className="app-setup__body">
                        {/* ---------- general ---------- */}
                        <section className="app-setup__section" aria-labelledby="as-general">
                          <h3 className="app-setup__section-title" id="as-general">
                            General
                          </h3>
                          <div className="app-setup__grid">
                            <SetupField {...fieldMeta("website_name")} label="Website Name">
                              <Form.Control
                                type="text"
                                placeholder="Website Name"
                                {...fieldProps("website_name")}
                                {...register("website_name", {
                                  required: "Website name Is Required",
                                  validate: (value) =>
                                    textLength(value) <= 200 ||
                                    "Data must be 200 characters or less",
                                })}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("email")} label="Email">
                              <Form.Control
                                type="text"
                                placeholder="Email"
                                {...fieldProps("email")}
                                {...register("email", {
                                  required: "Email is required",
                                  pattern: {
                                    value: EMAIL_PATTERN,
                                    message: "Invalid Email address",
                                  },
                                  validate: (value) => {
                                    const match = String(value ?? "").match(EMAIL_PATTERN);
                                    if (match) {
                                      const domainParts = match[1].split(".");
                                      const tld = match[2];
                                      if (domainParts[domainParts.length - 1] === tld) {
                                        return "Domain and top-level domain must be different";
                                      }
                                    }
                                    return true;
                                  },
                                })}
                                onKeyDown={(e) => {
                                  if (e.key === " ") e.preventDefault();
                                }}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("contact_no")} label="Contact No" hint="10 digits">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Mobile Number"
                                {...fieldProps("contact_no")}
                                {...register("contact_no", {
                                  required: "Mobile Number is required",
                                  validate: (value) =>
                                    textLength(value) === 10 ||
                                    " Contact number must be exactly 10 digits",
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("alt_contact_no")} label="Alternate Contact No" hint="10 digits">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Mobile Number"
                                {...fieldProps("alt_contact_no")}
                                {...register("alt_contact_no", {
                                  required: "Mobile Number is required",
                                  validate: (value) =>
                                    textLength(value) === 10 ||
                                    " Contact number must be exactly 10 digits",
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>
                          </div>
                        </section>

                        {/* ---------- location ---------- */}
                        <section className="app-setup__section" aria-labelledby="as-location">
                          <h3 className="app-setup__section-title" id="as-location">
                            Location
                          </h3>
                          <div className="app-setup__grid">
                            <SetupField {...fieldMeta("lat")} label="Latitude">
                              <Form.Control
                                type="text"
                                placeholder="lat"
                                {...fieldProps("lat")}
                                {...register("lat", {
                                  required: "lat Is Required",
                                  validate: (value) =>
                                    textLength(value) <= 200 ||
                                    "Data must be 200 characters or less",
                                })}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("long")} label="Longitude">
                              <Form.Control
                                type="text"
                                placeholder="long"
                                {...fieldProps("long")}
                                {...register("long", {
                                  required: "long Is Required",
                                  validate: (value) =>
                                    textLength(value) <= 200 ||
                                    "Data must be 200 characters or less",
                                })}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("address")} label="Address" full>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Address"
                                {...fieldProps("address")}
                                {...register("address", {
                                  required: "Address Is Required",
                                })}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("second_address")} label="Second Address" full>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Address"
                                {...fieldProps("second_address")}
                                {...register("second_address", {
                                  required: "Address Is Required",
                                })}
                              />
                            </SetupField>
                          </div>
                        </section>

                        {/* ---------- orders & delivery ---------- */}
                        <section className="app-setup__section" aria-labelledby="as-orders">
                          <h3 className="app-setup__section-title" id="as-orders">
                            Orders &amp; Delivery
                          </h3>
                          <div className="app-setup__grid">
                            <SetupField {...fieldMeta("minimum_order")} label="Minimum Order (price)">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Minimum Order"
                                {...fieldProps("minimum_order")}
                                {...register("minimum_order", {
                                  required: { message: "days are required " },
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("delivery_price")} label="Delivery Price">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Delivery Price"
                                {...fieldProps("delivery_price")}
                                {...register("delivery_price", {
                                  required: { message: "price are required " },
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("cancel_period")} label="Cancel Period (hours)">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Cancel Period"
                                {...fieldProps("cancel_period")}
                                {...register("cancel_period", {
                                  required: { message: "Cancel Period required " },
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("return_period")} label="Return Period (days)">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Return Period"
                                {...fieldProps("return_period")}
                                {...register("return_period", {
                                  required: { message: "Return Period required " },
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("return_charges")} label="Return Charges (percentage)">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Return Charges"
                                {...fieldProps("return_charges")}
                                {...register("return_charges", {
                                  required: { message: "Return Charges required " },
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("low_stock_day")} label="Low Stock Quantity Alert">
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Enter Low Stock Quantity"
                                {...fieldProps("low_stock_day")}
                                {...register("low_stock_day", {
                                  required: { message: "Low Stock Quantity is required " },
                                })}
                                onKeyDown={digitsOnly}
                              />
                            </SetupField>
                          </div>
                        </section>

                        {/* ---------- shiprocket ---------- */}
                        <section className="app-setup__section" aria-labelledby="as-shipping">
                          <h3 className="app-setup__section-title" id="as-shipping">
                            Shiprocket Pickup
                          </h3>
                          <div className="app-setup__grid">
                            <SetupField {...fieldMeta("ship_rocket_pickup_location")} label="Pickup Location">
                              <Form.Control
                                type="text"
                                maxLength={200}
                                placeholder="Enter Pickup Location"
                                {...fieldProps("ship_rocket_pickup_location")}
                                {...register("ship_rocket_pickup_location", {
                                  required: "Ship Rocket Pickup Location is required",
                                  maxLength: {
                                    value: 200,
                                    message: "Pickup Location cannot exceed 200 characters",
                                  },
                                })}
                              />
                            </SetupField>

                            <SetupField {...fieldMeta("ship_rocket_pickup_pincode")}
                              label="Pickup Pincode"
                              hint="6 digits"
                            >
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="Enter Pickup Pincode"
                                {...fieldProps("ship_rocket_pickup_pincode")}
                                {...register("ship_rocket_pickup_pincode", {
                                  required: "Ship Rocket Pickup Pincode is required",
                                  pattern: {
                                    value: /^\d{6}$/,
                                    message: "Pincode must contain exactly 6 digits",
                                  },
                                })}
                                onInput={(e) => {
                                  e.target.value = e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 6);
                                }}
                              />
                            </SetupField>
                          </div>
                        </section>

                        {/* ---------- branding ---------- */}
                        <section className="app-setup__section" aria-labelledby="as-branding">
                          <h3 className="app-setup__section-title" id="as-branding">
                            Branding
                          </h3>
                          <div className="app-setup__grid">
                            <SetupField {...fieldMeta("logo")} label="Logo" hint="PNG or JPG">
                              <div className="app-setup__media">
                                <div className="app-setup__preview">
                                  {logoPreview ? (
                                    <img src={logoPreview} alt="Logo preview" />
                                  ) : (
                                    <span>No logo</span>
                                  )}
                                </div>
                                {isEditing && (
                                  <Form.Control
                                    type="file"
                                    accept=".png, .jpg, .jpeg"
                                    id="app-setup-logo"
                                    className={classNames("app-setup__input", {
                                      "is-invalid": errors?.logo,
                                    })}
                                    {...register("logo", {})}
                                  />
                                )}
                              </div>
                            </SetupField>

                            <SetupField {...fieldMeta("qr_image")} label="QR Image" hint="PNG or JPG">
                              <div className="app-setup__media">
                                <div className="app-setup__preview">
                                  {qrPreview ? (
                                    <img src={qrPreview} alt="QR image preview" />
                                  ) : (
                                    <span>No QR image</span>
                                  )}
                                </div>
                                {isEditing && (
                                  <Form.Control
                                    type="file"
                                    accept=".png, .jpg, .jpeg"
                                    id="app-setup-qr_image"
                                    className={classNames("app-setup__input", {
                                      "is-invalid": errors?.qr_image,
                                    })}
                                    {...register("qr_image", {})}
                                  />
                                )}
                              </div>
                            </SetupField>
                          </div>
                        </section>
                      </fieldset>

                      {isEditing && (
                        <div className="app-setup__footer">
                          <button
                            type="button"
                            className="btn app-setup__btn app-setup__btn--ghost"
                            onClick={cancelEditing}
                            disabled={saving}
                          >
                            <CloseIcon fontSize="small" />
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn app-setup__btn app-setup__btn--primary"
                            disabled={saving}
                          >
                            {saving ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <SaveOutlinedIcon fontSize="small" />
                            )}
                            {saving ? "Saving…" : "Save"}
                          </button>
                        </div>
                      )}
                    </Form>
                  )}
                </div>
              </section>
            </div>
          </div>
        </section>
      </div>

      <ModalSave
        message={showModal.message}
        showErrorModal={showModal.code ? true : false}
      />
      <Toaster position="top-right" />
    </>
  );
};

export default Tables;
